<template>
    <span ref="root"
        class="ath-visual-sparkline"
        :class="{ 'el--fit': fitWidth }"
        :style="{ ...uiRootSize, ...uiColorVars }">
        <!-- Exactly the drawn size, so the hover tooltip lines up when a fitted sparkline sits in a wider space -->
        <span v-if="uiShowPlot"
            class="el-plot"
            :style="{ width: `${uiWidth}px`, height: `${height}px` }">
            <svg :width="uiWidth"
                :height="height"
                :viewBox="`0 0 ${uiWidth} ${height}`"
                role="img"
                :aria-label="uiAriaLabel"
                @mousemove="onMouseMove"
                @mouseleave="hoverIndex = null">
                <path v-if="fill && uiPoints.length > 1"
                    class="el-area"
                    :d="uiAreaPath" />
                <polyline v-if="uiPoints.length > 1"
                    class="el-line"
                    :points="uiLinePoints" />
                <line v-if="uiHoverPoint"
                    class="el-crosshair"
                    :x1="uiHoverPoint.x"
                    :x2="uiHoverPoint.x"
                    y1="0"
                    :y2="height" />
                <circle v-if="uiHoverPoint"
                    class="el-point"
                    :cx="uiHoverPoint.x"
                    :cy="uiHoverPoint.y"
                    :r="POINT_RADIUS" />
                <circle v-else-if="showEndPoint && uiEndPoint"
                    class="el-point"
                    :cx="uiEndPoint.x"
                    :cy="uiEndPoint.y"
                    :r="POINT_RADIUS" />
            </svg>
            <span v-if="uiHoverPoint"
                class="el-tooltip"
                :style="{ left: `${uiHoverPoint.x}px` }">
                {{ uiTooltip }}
            </span>
        </span>
    </span>
</template>

<script>
//  Radius of the end and hover points; the line is inset by this plus its ring so points aren't clipped
const POINT_RADIUS = 4;
const INSET = POINT_RADIUS + 2;
//  Narrower than this a fitted sparkline can't show a trend, so it isn't drawn
const MIN_FIT_WIDTH = 48;

/**
 * A small, axis-less line chart showing the shape of a series, with the latest
 * value marked and a tooltip on hover
 */
export default {
    name: 'mead-sparkline',
    props: {
        /**
         * Series to plot, oldest first, as `{value, label}` entries. `value` is plotted as a
         * number and `label` (e.g. a date) is optional, shown in the hover tooltip.
         */
        values: { type: Array, default: () => [] },
        /**
         * What the series is, used for the screen reader description. Passed through I18N.go.
         */
        label: { type: String, default: '' },
        /**
         * Width of the sparkline in pixels. With `fitWidth` this is the widest it's drawn.
         */
        width: { type: Number, default: 120 },
        /**
         * Draws the sparkline to fit the space its parent gives it, up to `width`, instead of always
         * `width` wide, so it never overflows a narrow container. It takes the space left over in a flex
         * row (or the full width of a block), sits at its right edge, and isn't drawn when there's too
         * little room to show a trend.
         */
        fitWidth: { type: Boolean, default: false },
        /**
         * Height of the sparkline in pixels.
         */
        height: { type: Number, default: 32 },
        /**
         * Color of the line and points: a COLOR_STYLES value (e.g. "primary"), a tint of one
         * (e.g. "primary-20") or any CSS color (e.g. "#BAC3C4"). When empty the line is muted and
         * the latest point uses the brand color.
         */
        color: { type: String, default: '' },
        /**
         * Shades the area under the line.
         */
        fill: { type: Boolean, default: false },
        /**
         * Marks the latest value with a point.
         */
        showEndPoint: { type: Boolean, default: true },
        /**
         * Formats values for the tooltip and screen reader description. Receives the
         * entry's original `value`.
         */
        valueFormatter: { type: Function, default: (value) => `${value}` },
    },
    data: () => ({
        POINT_RADIUS,
        hoverIndex: null,
        //  Width of the root in pixels, measured while fitWidth is on
        availableWidth: null,
        resizeObserver: null,
    }),
    computed: {
        uiWidth: function () {
            if (!this.fitWidth || this.availableWidth === null) return this.width;
            return Math.min(this.width, this.availableWidth);
        },
        uiShowPlot: function () {
            return !this.fitWidth || this.uiWidth >= MIN_FIT_WIDTH;
        },
        //  A fitted root is sized by its parent, never by the plot in it, so measuring it can't feed back into its size
        uiRootSize: function () {
            return this.fitWidth
                ? { height: `${this.height}px` }
                : { width: `${this.width}px`, height: `${this.height}px` };
        },
        uiColorVars: function () {
            const color = MeadCommon.resolveColor(this.color);
            return color ? { '--sparkline-line': color.bg, '--sparkline-point': color.bg } : {};
        },
        uiNumbers: function () {
            return this.values.map(({ value }) => Number(value));
        },
        uiPoints: function () {
            const count = this.uiNumbers.length;
            if (count === 0) return [];

            const min = Math.min(...this.uiNumbers);
            const max = Math.max(...this.uiNumbers);
            const plotWidth = this.uiWidth - INSET * 2;
            const plotHeight = this.height - INSET * 2;

            return this.uiNumbers.map((value, index) => ({
                //  A single value sits in the middle, a flat series runs through the vertical middle
                x:
                    INSET +
                    (count === 1
                        ? plotWidth / 2
                        : (index / (count - 1)) * plotWidth),
                y:
                    INSET +
                    (max === min
                        ? plotHeight / 2
                        : (1 - (value - min) / (max - min)) * plotHeight),
            }));
        },
        uiLinePoints: function () {
            return this.uiPoints.map(({ x, y }) => `${x},${y}`).join(' ');
        },
        uiAreaPath: function () {
            const first = this.uiPoints[0];
            const last = this.uiPoints[this.uiPoints.length - 1];
            const bottom = this.height - INSET;
            return `M${first.x},${bottom} L${this.uiLinePoints.replace(
                / /g,
                ' L'
            )} L${last.x},${bottom} Z`;
        },
        uiEndPoint: function () {
            return this.uiPoints[this.uiPoints.length - 1] || null;
        },
        uiHoverPoint: function () {
            return this.hoverIndex === null
                ? null
                : this.uiPoints[this.hoverIndex] || null;
        },
        uiTooltip: function () {
            const { value, label } = this.values[this.hoverIndex];
            const formatted = this.valueFormatter(value);
            return label || label === 0 ? `${label}: ${formatted}` : formatted;
        },
        uiAriaLabel: function () {
            const label = this.label ? MeadCommon.I18N.go(this.label) : '';
            if (this.values.length === 0) return label;

            const first = this.valueFormatter(this.values[0].value);
            const last = this.valueFormatter(
                this.values[this.values.length - 1].value
            );
            const summary =
                this.values.length === 1 ? last : `${first} → ${last}`;
            return label ? `${label}: ${summary}` : summary;
        },
    },
    watch: {
        fitWidth: function () {
            this.observeWidth();
        },
    },
    mounted: function () {
        this.observeWidth();
    },
    beforeDestroy: function () {
        this.resizeObserver?.disconnect();
    },
    methods: {
        /**
         * Tracks the root's width while fitWidth is on, and stops when it's off
         */
        observeWidth: function () {
            this.resizeObserver?.disconnect();
            this.resizeObserver = null;
            this.availableWidth = null;
            if (!this.fitWidth) return;

            this.resizeObserver = new ResizeObserver(([entry]) => {
                this.availableWidth = Math.floor(entry.contentRect.width);
            });
            this.resizeObserver.observe(this.$refs.root);
        },
        onMouseMove: function (event) {
            if (this.uiPoints.length === 0) return;

            //  Snap to the nearest point along x
            const x =
                event.clientX -
                event.currentTarget.getBoundingClientRect().left;
            let nearest = 0;
            this.uiPoints.forEach((point, index) => {
                if (
                    Math.abs(point.x - x) <
                        Math.abs(this.uiPoints[nearest].x - x)
                )
                    nearest = index;
            });
            this.hoverIndex = nearest;
        },
    },
};
</script>
<style lang="scss">
@import '../../../../sass/_variables.scss';

.ath-visual-sparkline {
    --sparkline-line: var(--c-txt-light);
    --sparkline-point: var(--c-brand-main);

    display: inline-block;
    position: relative;
    flex-shrink: 0;
    vertical-align: middle;

    &.el--fit {
        display: flex;
        justify-content: flex-end;
        flex: 1 1 0;
        min-width: 0;
        width: 100%;
    }

    .el-plot {
        display: block;
        position: relative;
    }

    svg {
        display: block;
        overflow: visible;
    }

    .el-line {
        fill: none;
        stroke: var(--sparkline-line);
        stroke-width: 2;
        stroke-linejoin: round;
        stroke-linecap: round;
    }

    .el-area {
        fill: color-mix(in srgb, var(--sparkline-line) 15%, transparent);
    }

    .el-crosshair {
        stroke: var(--c-panel-border);
        stroke-width: 1;
    }

    .el-point {
        fill: var(--sparkline-point);
        stroke: var(--c-panel-bg);
        stroke-width: 2;
    }

    .el-tooltip {
        position: absolute;
        bottom: calc(100% + #{$s-xs});
        transform: translateX(-50%);
        padding: 0.4rem 0.8rem;
        border-radius: $br-m;
        background-color: var(--c-txt);
        color: var(--c-panel-bg);
        font-size: $fs-s;
        line-height: 1.2;
        white-space: nowrap;
        pointer-events: none;
        z-index: 1;
    }

}
</style>
